/**
 * Sign up page — /signup
 *
 * Marketing-style account creation. Web layout is a two-column split: the
 * signup UI (SSO promoted above the email form) on the left, a branded panel
 * on the right with a product mock and the post-signup steps over an animated
 * mesh-gradient backdrop. Columns stack on mobile with the form first.
 *
 * On desktop the page is pinned to the viewport height: the form column stays
 * fully visible and only the right panel scrolls if its content overflows.
 *
 * After successful signup, sends to:
 * 1. Email verification (if enabled)
 * 2. Or directly to redirect_uri (if verification is disabled/not required)
 */
import { createFileRoute, Link, Navigate, useNavigate } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@better-bookkeeping/ui/button";
import { Input } from "@better-bookkeeping/ui/input";
import { Label } from "@better-bookkeeping/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@better-bookkeeping/ui/alert";
import { Skeleton } from "@better-bookkeeping/ui/skeleton";
import { AlertCircle, MailCheck } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { motion, MotionConfig, type Variants } from "motion/react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { authClient, invitationClient, orpcClient } from "@/lib/auth-client";
import { z } from "zod";
import { DevEmailPreview } from "@/components/dev-email-preview";
import { GoogleIcon } from "@/components/google-icon";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";
import { PasswordInput } from "@better-bookkeeping/ui/password-input";
import { VisorWordmark } from "@better-bookkeeping/ui/visor-logo";
import { MeshGradient } from "@paper-design/shaders-react";
import { buildEnrollMfaCallbackURL, buildGatewayCallbackURL } from "@/lib/auth-flow-redirects";
import { getSocialSignInError } from "@/lib/social-sign-in-error";
import { getSignupDefaultRedirect } from "@/lib/redirect-utils";
import { getSignupPasswordError, SIGNUP_PASSWORD_MIN_LENGTH } from "@/lib/signup-validation";
import {
  getPendingInvitation,
  preparePendingInvitationAuth,
  withInviteTokenSearch,
} from "@/lib/pending-pricing-invite";
import { selectSignupPrefill } from "@/lib/signup-prefill";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { useSession } from "@/hooks/useAuth";
import { AuthLoading } from "@/components/auth-loading";

export const Route = createFileRoute("/signup")({
  validateSearch: withInviteTokenSearch({
    redirect_uri: z.string().optional(),
  }).parse,
  component: SignupPage,
});

const SUBHEAD =
  "See how the full accounting system works — books, quarterly tax planning, and filing — with live sample data. No charges, no commitment.";

// Snappy, restrained easing borrowed from the marketing site's motion language.
const EASE = [0.22, 1, 0.36, 1] as const;

const rise: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};

const SIGNUP_STEPS = [
  {
    label: "Create your account and explore",
    summary: "See exactly how Visor works — no commitment, no billing, no pressure.",
  },
  {
    label: "Activate your service",
    summary:
      "Choose your billing cycle, review your service agreement, and add a payment method. Billing starts here.",
  },
  {
    label: "We take it from there",
    summary: "After activation, your Visor team gets to work. Books, taxes, reporting — handled.",
  },
] as const;

const INVITATION_SIGNUP_STEPS = [
  {
    label: "Create your account",
    summary: "Verify your email and set up secure sign-in to protect your account.",
  },
  {
    label: "Confirm your account",
    summary: "Confirm which account you'll use for this invitation.",
  },
  {
    label: "Review your invitation",
    summary: "Review the details, then accept or decline each invitation.",
  },
] as const;

const MOCK_TRANSACTIONS = [
  { name: "Stripe payout", category: "Income", amount: "+$5,400", positive: true },
  { name: "Gusto payroll", category: "Payroll", amount: "-$8,120", positive: false },
] as const;

const MOCK_BARS = [42, 58, 50, 70, 64, 82] as const;

const PANEL_SURFACE = "bg-brand-950 text-brand-100";

// Mesh-gradient palette lifted from the marketing site's hero shader.
const SHADER_COLORS = ["#134d2c", "#18794a", "#155536", "#22915a", "#0f5c35"] as const;

function SignupFormLoading() {
  return (
    <div
      className="space-y-4"
      aria-busy="true"
      aria-label="Preparing signup details"
      data-testid="signup-loading"
    >
      <p className="text-sm text-muted-foreground" role="status">
        Preparing your signup details…
      </p>
      {["name", "email", "password", "confirm-password"].map((field) => (
        <div key={field} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
      <Skeleton className="h-10 w-full" />
      <Skeleton className="mx-auto h-4 w-24" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function PanelEyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-highlight">
      {children}
    </span>
  );
}

// Ambient WebGL mesh gradient — the same shader the marketing hero uses, faded
// in (and dialed back) once the canvas paints so it reads as a subtle wash.
function PanelBackdrop() {
  return (
    <motion.div
      aria-hidden="true"
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.45 }}
      transition={{ duration: 1, ease: "easeOut" }}
      className="pointer-events-none absolute inset-0"
    >
      <MeshGradient
        speed={0.4}
        colors={[...SHADER_COLORS]}
        distortion={0.4}
        swirl={0.4}
        grainMixer={0.04}
        grainOverlay={0.02}
        style={{ width: "100%", height: "100%" }}
      />
    </motion.div>
  );
}

// Right-hand shell — the mesh-gradient backdrop stays fixed while the content
// scrolls inside on short viewports. Content is vertically centered when it
// fits and scrolls (without clipping) when it doesn't.
function GradientAside({ children }: { children: ReactNode }) {
  return (
    <motion.aside
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
      className={`relative overflow-hidden ${PANEL_SURFACE}`}
    >
      <PanelBackdrop />
      <div className="relative z-10 h-full overflow-y-auto">
        <div className="signup-scale flex min-h-full flex-col justify-center px-6 py-8 sm:px-10 lg:px-14">
          <div className="mx-auto w-full max-w-md">{children}</div>
        </div>
      </div>
    </motion.aside>
  );
}

function StepList({ isInvitation }: { isInvitation: boolean }) {
  return (
    <motion.ol variants={stagger} initial="hidden" animate="show" className="space-y-5">
      {(isInvitation ? INVITATION_SIGNUP_STEPS : SIGNUP_STEPS).map((step, index) => (
        <motion.li key={step.label} variants={rise} className="flex gap-4">
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-highlight/15 text-sm font-semibold text-highlight"
          >
            {index + 1}
          </span>
          <div className="space-y-1.5">
            <h3 className="font-medium text-brand-50">{step.label}</h3>
            <p className="text-sm leading-relaxed text-brand-200">{step.summary}</p>
          </div>
        </motion.li>
      ))}
    </motion.ol>
  );
}

function KpiCard({
  label,
  value,
  delta,
  accent,
}: {
  label: string;
  value: string;
  delta: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-2.5 shadow-sm ${
        accent ? "border-electric-500/30 bg-electric-500/5" : "border-border bg-card"
      }`}
    >
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-base font-semibold text-foreground">{value}</p>
      <p className="text-[10px] font-medium text-brand-600 dark:text-brand-400">{delta}</p>
    </div>
  );
}

// A stylized, non-interactive dashboard rendered entirely in markup — echoes
// the marketing site's browser-framed product mockups.
function DashboardWindow() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
      <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-4 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
        <span className="ml-3 hidden rounded-md bg-background px-2 py-0.5 text-[10px] text-muted-foreground sm:block">
          dashboard.withvisor.com
        </span>
      </div>
      <div className="space-y-2.5 p-3.5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Overview</p>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
            This quarter
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <KpiCard label="Net income" value="$48,250" delta="+12.4%" accent />
          <KpiCard label="Cash on hand" value="$126,400" delta="+3.1%" />
        </div>

        <div className="rounded-lg border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-medium text-foreground">Profit &amp; Loss</p>
            <p className="text-[10px] text-muted-foreground">Jan – Jun</p>
          </div>
          <div className="flex h-10 items-end gap-2">
            {MOCK_BARS.map((height, index) => (
              <motion.span
                key={index}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: 0.3 + index * 0.08, duration: 0.4, ease: EASE }}
                style={{ height: `${height}%` }}
                className="flex-1 origin-bottom rounded-sm bg-gradient-to-t from-brand-700 to-brand-500"
              />
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {MOCK_TRANSACTIONS.map((tx) => (
            <div
              key={tx.name}
              className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-1.5"
            >
              <div className="flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-muted" />
                <div className="space-y-0.5">
                  <p className="text-xs font-medium text-foreground">{tx.name}</p>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[9px] text-muted-foreground">
                    {tx.category}
                  </span>
                </div>
              </div>
              <p
                className={`text-xs font-semibold ${
                  tx.positive ? "text-brand-600 dark:text-brand-400" : "text-foreground"
                }`}
              >
                {tx.amount}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ShowcasePanel({ isInvitation }: { isInvitation: boolean }) {
  return (
    <GradientAside>
      <div className="space-y-5">
        <div className="space-y-1.5">
          <PanelEyebrow>
            {isInvitation ? "Getting started" : "See it with live sample data"}
          </PanelEyebrow>
          <h2 className="text-lg font-semibold tracking-tight text-brand-50">
            {isInvitation
              ? "You've been invited to Visor"
              : "Explore the full picture before you commit"}
          </h2>
        </div>
        {!isInvitation && <DashboardWindow />}
        <StepList isInvitation={isInvitation} />
      </div>
    </GradientAside>
  );
}

function SignupPage() {
  const { redirect_uri } = Route.useSearch();
  const navigate = useNavigate();
  const runtimeConfig = useRuntimeConfig();
  const sessionQuery = useSession();
  // New accounts land on the new dashboard by default; an explicit redirect_uri
  // (e.g. arriving from a specific app) always wins.
  const signupRedirect = redirect_uri ?? getSignupDefaultRedirect(runtimeConfig);
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const pendingInvitation = getPendingInvitation();
  const editedPrefillFields = useRef({ name: false, email: false });
  const packagePreviewQuery = useQuery({
    queryKey: ["invitation-package", "preview", pendingInvitation?.token],
    queryFn: () =>
      invitationClient.invitationPackage.preview({ token: pendingInvitation?.token ?? "" }),
    enabled: pendingInvitation?.kind === "package",
    retry: false,
  });
  const invitePreviewQuery = useQuery({
    queryKey: ["pricing-invite", "preview", pendingInvitation?.token],
    queryFn: () => orpcClient.pricingInvite.preview({ token: pendingInvitation?.token ?? "" }),
    enabled: pendingInvitation?.kind === "legacy",
    retry: false,
  });
  const hubSpotPrefillQuery = useQuery({
    queryKey: ["signup-attribution", "hubspot"],
    queryFn: () => orpcClient.signupAttribution.previewHubSpot({}),
    enabled: !pendingInvitation,
    retry: false,
  });

  const resetTurnstileChallenge = () => {
    turnstileRef.current?.reset();
    setTurnstileToken(null);
  };

  const retryTurnstileChallenge = () => {
    setTurnstileError(null);
    resetTurnstileChallenge();
  };

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const passwordError = getSignupPasswordError(value.password, value.confirmPassword);
        if (passwordError) {
          setError(passwordError);
          return;
        }

        if (runtimeConfig.turnstileSiteKey && !turnstileToken) {
          setTurnstileError("Complete the security check before creating your account.");
          return;
        }

        const prepared = await preparePendingInvitationAuth(undefined);
        const callbackURL = buildEnrollMfaCallbackURL(
          runtimeConfig,
          signupRedirect,
          prepared.continuationId,
        );

        const { data, error: signUpError } = await authClient.signUp.email(
          {
            name: value.name,
            email: value.email,
            password: value.password,
            callbackURL,
          },
          turnstileToken
            ? {
                headers: {
                  "x-captcha-response": turnstileToken,
                },
              }
            : undefined,
        );

        if (signUpError) {
          resetTurnstileChallenge();
          if (signUpError.status === 429 || signUpError.message?.includes("429")) {
            setError("Too many tries. Take a breath and try again in a minute.");
          } else if (
            signUpError.code === "MISSING_RESPONSE" ||
            signUpError.code === "VERIFICATION_FAILED"
          ) {
            setError("The security check could not be verified. Please try again.");
          } else {
            setError(signUpError.message || "We couldn't create your account. Try again.");
          }
          return;
        }

        // If token is null, email verification is required — show message
        if (data?.token === null || data?.user?.emailVerified === false) {
          setTurnstileToken(null);
          setSubmittedEmail(value.email);
          setEmailSent(true);
          return;
        }

        // Session created without email verification — bounce through "/"
        navigate({ to: "/", search: { redirect_uri: signupRedirect } });
      } catch (err) {
        resetTurnstileChallenge();
        console.error("Signup failed:", err);
        setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      }
    },
  });
  const invitePreview =
    pendingInvitation?.kind === "legacy" && invitePreviewQuery.data?.kind === "valid"
      ? invitePreviewQuery.data
      : null;
  const hubSpotPrefill =
    !pendingInvitation && hubSpotPrefillQuery.data?.kind === "found"
      ? hubSpotPrefillQuery.data
      : null;
  const packagePrefill =
    pendingInvitation?.kind === "package" && packagePreviewQuery.data
      ? { targetEmail: packagePreviewQuery.data.targetEmail, firstName: null, lastName: null }
      : null;
  const signupPrefill = selectSignupPrefill(packagePrefill ?? invitePreview, hubSpotPrefill);
  const invitationEmail = packagePrefill?.targetEmail ?? invitePreview?.targetEmail;
  const isInvitation = pendingInvitation !== null;
  let isSignupPrefillLoading = hubSpotPrefillQuery.isPending;
  if (pendingInvitation?.kind === "legacy") {
    isSignupPrefillLoading = invitePreviewQuery.isPending;
  } else if (pendingInvitation?.kind === "package") {
    isSignupPrefillLoading = packagePreviewQuery.isPending;
  }

  useLayoutEffect(() => {
    if (!signupPrefill) return;
    if (!editedPrefillFields.current.email && !form.state.values.email) {
      form.setFieldValue("email", signupPrefill.email);
    }
    if (!editedPrefillFields.current.name && !form.state.values.name) {
      if (signupPrefill.name) form.setFieldValue("name", signupPrefill.name);
    }
  }, [form, signupPrefill]);

  if (sessionQuery.isLoading) return <AuthLoading />;
  if (sessionQuery.data) {
    return <Navigate to="/" search={{ redirect_uri: signupRedirect }} replace />;
  }

  const googleButton = (
    <Button
      type="button"
      variant="outline"
      className="group h-11 w-full transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
      data-testid="signup-google-button"
      onClick={async () => {
        // AUTH-132: route OAuth callback through gateway "/" so the
        // single post-sign-in decision tree applies.
        setError(null);
        try {
          const prepared = await preparePendingInvitationAuth(undefined);
          const callbackURL = buildGatewayCallbackURL(
            runtimeConfig,
            signupRedirect,
            prepared.continuationId,
          );
          const { data, error: socialError } = await authClient.signIn.social({
            provider: "google",
            callbackURL,
          });
          if (socialError) {
            setError(getSocialSignInError(socialError));
            return;
          }
          if (data?.url && data?.redirect) {
            window.location.href = data.url;
          }
        } catch {
          setError(
            isInvitation
              ? "Couldn't start sign-in. Reopen the invitation link and try again."
              : "We couldn't start Google sign-in. Try again.",
          );
        }
      }}
    >
      <GoogleIcon className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
      Continue with Google
    </Button>
  );

  const emailForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="space-y-4"
      data-testid="signup-form"
    >
      <div className="space-y-2">
        <Label htmlFor="name">Your name</Label>
        <form.Field
          name="name"
          validators={{ onChange: z.string().min(1, "Name is required") }}
          children={(field) => (
            <div>
              <Input
                id="name"
                type="text"
                autoComplete="name"
                value={field.state.value}
                onChange={(e) => {
                  editedPrefillFields.current.name = true;
                  field.handleChange(e.target.value);
                }}
                placeholder="Jane Smith"
                required
                data-testid="signup-name-input"
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive mt-1" data-testid="signup-name-error">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <form.Field
          name="email"
          validators={{ onChange: z.string().email("Please enter a valid email") }}
          children={(field) => (
            <div>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-describedby={invitationEmail ? "signup-invitation-email-hint" : undefined}
                value={field.state.value}
                onChange={(e) => {
                  editedPrefillFields.current.email = true;
                  field.handleChange(e.target.value);
                }}
                placeholder="you@example.com"
                required
                data-testid="signup-email-input"
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive mt-1" data-testid="signup-email-error">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message}
                </p>
              )}
            </div>
          )}
        />
        {invitationEmail && (
          <p
            id="signup-invitation-email-hint"
            className="text-xs text-muted-foreground"
            data-testid="signup-invitation-email-hint"
          >
            This invitation was sent to {invitationEmail}. You can use a different email and confirm
            which account to use after signing in.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <form.Field
          name="password"
          validators={{
            onChange: z.string().min(SIGNUP_PASSWORD_MIN_LENGTH, "Use at least 12 characters."),
          }}
          children={(field) => (
            <div className="space-y-2">
              <PasswordInput
                id="password"
                autoComplete="new-password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="At least 12 characters"
                required
                data-testid="signup-password-input"
              />
              <PasswordStrengthMeter
                value={field.state.value}
                data-testid="signup-password-strength-section"
              />
              {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                <p className="text-sm text-destructive" data-testid="signup-password-error">
                  {typeof field.state.meta.errors[0] === "string"
                    ? field.state.meta.errors[0]
                    : field.state.meta.errors[0]?.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm password</Label>
        <form.Field
          name="confirmPassword"
          children={(field) => (
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="Re-enter your password"
              required
              data-testid="signup-confirm-password-input"
            />
          )}
        />
      </div>

      {runtimeConfig.turnstileSiteKey && (
        <div className="space-y-2">
          <Turnstile
            ref={turnstileRef}
            siteKey={runtimeConfig.turnstileSiteKey}
            className="w-full"
            onSuccess={(token) => {
              setTurnstileToken(token);
              setTurnstileError(null);
              setError(null);
            }}
            onExpire={() => {
              setTurnstileToken(null);
              setTurnstileError("The security check expired. Please complete it again.");
            }}
            onError={(errorCode) => {
              console.warn("Turnstile challenge failed", { errorCode });
              setTurnstileToken(null);
              setTurnstileError("We couldn't complete the security check. Please try again.");
            }}
            onTimeout={() => {
              setTurnstileToken(null);
              setTurnstileError("The security check timed out. Please complete it again.");
            }}
            onUnsupported={() => {
              setTurnstileToken(null);
              setTurnstileError(
                "This browser can't run the security check. Try a supported browser.",
              );
            }}
            options={{
              appearance: "interaction-only",
              refreshExpired: "auto",
              size: "flexible",
              theme: "auto",
            }}
          />
          {turnstileError && (
            <div
              className="flex items-center justify-between gap-3"
              role="alert"
              data-testid="signup-turnstile-error"
            >
              <p className="text-sm text-destructive">{turnstileError}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={retryTurnstileChallenge}
                data-testid="signup-turnstile-retry-button"
              >
                Try again
              </Button>
            </div>
          )}
        </div>
      )}

      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
        children={([canSubmit, isSubmitting]) => (
          <Button
            type="submit"
            disabled={
              !canSubmit ||
              isSubmitting ||
              (Boolean(runtimeConfig.turnstileSiteKey) && !turnstileToken)
            }
            className="h-11 w-full transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
            data-testid="signup-submit-button"
          >
            {isSubmitting ? "Creating account…" : "Create account"}
          </Button>
        )}
      />
    </form>
  );

  const emailSentBlock = (
    <div className="space-y-4">
      <Alert variant="success" data-testid="signup-email-sent-state">
        <MailCheck className="h-4 w-4" />
        <AlertTitle>Check your inbox</AlertTitle>
        <AlertDescription>
          We sent a verification link to{" "}
          <span className="font-medium text-foreground">{submittedEmail}</span>. Open it to continue{" "}
          {isInvitation
            ? "setting up secure sign-in. You'll then confirm your account and review your invitation before accepting anything."
            : "setting up your sign-in security."}
        </AlertDescription>
      </Alert>
      <DevEmailPreview data-testid="signup-dev-email-preview" />
      <Button
        variant="outline"
        className="w-full"
        onClick={() => navigate({ to: "/login", search: { redirect_uri } })}
        data-testid="signup-continue-to-login-button"
      >
        Continue to sign in
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        Didn't get the email? Check spam, or{" "}
        <button
          type="button"
          onClick={() => {
            setEmailSent(false);
            setSubmittedEmail(null);
            setTurnstileToken(null);
            setTurnstileError(null);
          }}
          className="text-primary hover:underline"
          data-testid="signup-try-again-button"
        >
          try again
        </button>
        .
      </p>
    </div>
  );

  const errorAlert = error ? (
    <Alert variant="destructive" data-testid="signup-error">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : null;

  let body: ReactNode;
  if (emailSent) {
    body = emailSentBlock;
  } else if (isSignupPrefillLoading) {
    body = <SignupFormLoading />;
  } else {
    body = (
      <div className="space-y-6">
        <div className="space-y-2">
          <p className="text-center text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Fastest way to start
          </p>
          {googleButton}
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-background px-2 text-muted-foreground">or sign up with email</span>
          </div>
        </div>

        {emailForm}

        <p className="text-center text-xs text-muted-foreground leading-relaxed">
          By signing up, you are indicating that you have read and agree to the{" "}
          <a
            href="https://withvisor.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
            data-testid="signup-terms-link"
          >
            Terms of Use
          </a>{" "}
          and{" "}
          <a
            href="https://withvisor.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 hover:text-foreground"
            data-testid="signup-privacy-link"
          >
            Privacy Policy
          </a>
          .
        </p>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            search={{ redirect_uri }}
            className="text-primary hover:underline font-medium"
            data-testid="signup-login-link"
          >
            Sign in now
          </Link>
          .
        </p>
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-dvh bg-background lg:grid lg:h-dvh lg:grid-cols-2 lg:overflow-hidden">
        {/* Mobile: sit just under the viewport so the green panel peeks below,
            hinting there's more content. Desktop: full-height column that
            centers the form when it fits and scrolls (without clipping) when it
            doesn't. */}
        <div className="lg:h-dvh lg:overflow-y-auto">
          <div className="signup-scale flex min-h-[calc(100svh-3.5rem)] flex-col justify-center px-6 py-6 sm:px-10 lg:min-h-full lg:py-8">
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="mx-auto w-full max-w-md space-y-6"
            >
              <motion.div variants={rise}>
                <VisorWordmark
                  className="h-8 w-auto"
                  classNameLetters="dark:fill-foreground"
                  classNameVBottom="dark:fill-foreground"
                  classNameVTop="dark:fill-foreground"
                />
              </motion.div>

              <div className="space-y-6">
                <motion.div variants={rise} className="space-y-1.5">
                  <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-brand-700 dark:text-brand-400">
                    {isInvitation ? "You've been invited" : "Get started"}
                  </span>
                  <h1 className="text-2xl font-semibold tracking-tight lg:text-3xl">
                    {isInvitation ? "Create your account" : "Create your Visor account"}
                  </h1>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {isInvitation
                      ? "Create an account and verify your email to review and accept your invitation."
                      : SUBHEAD}
                  </p>
                </motion.div>

                {errorAlert && <motion.div variants={rise}>{errorAlert}</motion.div>}

                <motion.div variants={rise}>{body}</motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        <ShowcasePanel isInvitation={isInvitation} />
      </div>
    </MotionConfig>
  );
}
